import { Button, HStack, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { Tag as TagIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

type PromoCodeInputProps = {
  value: string;
  isLoading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onApply: () => void;
};

/**
 * Empty/default promo state, per `Ticket Zone Checkout Summary.png`: a **pill-shaped** field with
 * the tag icon inside it, and a separate **outlined pill** for "تطبيق" on the inline-end.
 *
 * Visual only (TZ-CO-8) — every handler is passed straight through from `usePromoCode`, and the
 * `aria-label`s are unchanged so the behaviour lock in `PromoCode.test.tsx` still addresses the
 * same elements.
 */
export const PromoCodeInput = ({
  value,
  isLoading,
  error,
  onChange,
  onKeyDown,
  onApply,
}: PromoCodeInputProps) => {
  const { formatMessage } = useIntl();

  return (
    <HStack spacing='8px' align='center' dir='rtl'>
      <InputGroup flex='1'>
        {/* The tag sits at the field's **left** end with the placeholder right-aligned away from
            it, per the design — not tucked against the text it labels. */}
        <InputLeftElement pointerEvents='none' h='48px' pl='14px'>
          <TagIcon size={18} color='#A0AEC0' strokeWidth={2} aria-hidden />
        </InputLeftElement>
        <Input
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={formatMessage({
            id: 'checkout.promoCode.placeholder',
            defaultMessage: 'Enter promo code',
          })}
          isDisabled={isLoading}
          aria-label={formatMessage({
            id: 'checkout.promoCode.input',
            defaultMessage: 'Promo code input',
          })}
          // Chakra's own invalid flag rather than a hand-set `aria-invalid`: `Input` derives that
          // attribute from its form-control state and drops a raw prop, so setting it directly
          // left the field styled red but not announced as invalid.
          isInvalid={Boolean(error)}
          errorBorderColor='red'
          textAlign='right'
          h='48px'
          borderRadius='full'
          pl='44px'
          _placeholder={{ color: 'dark.D400' }}
          borderColor='dark.D150'
          _hover={{ borderColor: error ? 'red' : 'dark.D200' }}
          focusBorderColor={error ? 'red' : 'green.G500'}
        />
      </InputGroup>
      <Button
        type='button'
        variant='outline'
        onClick={onApply}
        isDisabled={!value.trim() || isLoading}
        isLoading={isLoading}
        h='48px'
        minW='88px'
        borderRadius='full'
        borderColor='dark.D150'
        color='dark.D800'
        bg='white'
        _hover={{ bg: 'dark.D50' }}
        _active={{ bg: 'dark.D100' }}
        aria-label={formatMessage({
          id: 'checkout.promoCode.apply',
          defaultMessage: 'Apply promo code',
        })}
      >
        <FormattedMessage id='checkout.promoCode.apply' defaultMessage='Apply' />
      </Button>
    </HStack>
  );
};
